import ReactDOM from "react-dom/client";

import {
  extraErrorDataIntegration,
  httpClientIntegration,
} from "@sentry/react";
import * as Sentry from "@sentry/react";
import React from "react";
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from "react-router-dom";

import {
  KINDE_ENVIRONMENT,
  KindeAuthenticatedUI,
  KindeRedirectProvider,
} from "@inrange/shared-components";

import { ReloginProvider } from "@inrange/building-manager-api-client";
import { GlobalStyle, ThemeProvider } from "@inrange/theme-components/styles";
import { QueryClientProvider } from "@tanstack/react-query";
import AppRoutes from "./AppRoutes";
import { UserProvider } from "./auth/UserContext";
import queryClient from "./queryClient";

if (import.meta.env.PROD) {
  Sentry.init({
    dsn: "https://eb8365ed1ef648f99ae52eb6bc21529a@o1336164.ingest.sentry.io/4504717185777664",
    beforeSend(event) {
      // Filter out bogus errors from Outlook SafeLink / email link scanners.
      // These use a .NET WebBrowser control that emits COM interop errors like:
      // "Object Not Found Matching Id:X, MethodName:Y, ParamCount:Z"
      // See: https://github.com/getsentry/sentry-javascript/issues/3440
      const exception = event.exception?.values?.[0];
      const message = event.message || exception?.value || "";
      const exceptionType = exception?.type || "";

      if (/Object Not Found Matching Id:\d+, MethodName:\w+, ParamCount:\d+/.test(message)) {
        return null;
      }

      // Transient axios network errors, timeouts, and aborted requests are
      // user-side / connectivity issues, not application bugs (see
      // CUSTOMER-APP-C1/C4/C5/BV).
      if (
        exceptionType === "AxiosError" &&
        (message === "Network Error" ||
          message === "Request aborted" ||
          message === "canceled" ||
          /timeout/i.test(message) ||
          /aborted/i.test(message))
      ) {
        return null;
      }

      // Leaflet / GoogleMutant lifecycle races (see CUSTOMER-APP-C3).
      // Known upstream bug (https://github.com/Leaflet/Leaflet/issues/9527).
      //
      // Don't filter by stack-frame filename — at beforeSend time the SDK
      // sees the raw minified bundle URL (e.g. /assets/index-XXX.js), not
      // the post-symbolication Leaflet path. The message patterns below are
      // distinctive enough to stand alone.
      if (
        exceptionType === "TypeError" &&
        (/_leaflet_pos/.test(message) ||
          /_controlCorners/.test(message) ||
          /this\._map is null/.test(message) ||
          /controls_ready/.test(message))
      ) {
        return null;
      }

      // GoogleMutant's `_setupGoogleMap` polls for `window.google` and throws
      // a plain Error (not TypeError) after 10 s if it never loads.
      if (/window\.google not found/.test(message)) {
        return null;
      }

      // Google Maps init races — the Maps JS SDK emits `Could not load
      // "<component>"` when a component fails to load from the Maps CDN
      // (network blip, ad-blocker, etc.).
      if (/Could not load "(controls|places|marker|geometry|drawing)"/.test(message)) {
        return null;
      }

      return event;
    },
    integrations: [
      // https://docs.sentry.io/platforms/javascript/guides/react/features/react-router/
      Sentry.reactRouterV7BrowserTracingIntegration({
        useEffect: React.useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      Sentry.replayIntegration(),
      // 4xx is the application's responsibility - we already capture the
      // errors we care about via queryCache.onError in queryClient.ts (which
      // covers axios + react-query, the path nearly every XHR takes here).
      // Keeping 4xx in httpClientIntegration on top of that gave us duplicate
      // captures: a 401 was reported once via queryCache and once as an
      // unhandled http.client error, with the latter accumulating fast since
      // it groups by URL pattern. CUSTOMER-APP-P hit ~12k events that way.
      // Restrict to 5xx so we keep a defence-in-depth net for genuine server
      // errors from any XHR source (third-party scripts, future fetch() calls)
      // without doubling up on 4xx.
      httpClientIntegration({
        failedRequestStatusCodes: [[500, 599]],
      }),
      extraErrorDataIntegration(),
    ],
    tracesSampleRate: 1.0,
    replaysOnErrorSampleRate: 1.0,
    environment: import.meta.env.VITE_BRANCH_NAME,
  });
}

const isDangerouslyUseLocalStorage =
  !import.meta.env.PROD && import.meta.env.VITE_TEST_MODE;
if (isDangerouslyUseLocalStorage) {
  // Kinde v5 stores the refresh token under `kinde-refreshToken0` (was
  // `kinde_refresh_token` in v3), so look up the refreshToken-suffixed key.
  const refreshTokenKey = Object.keys(window.localStorage).find((k) =>
    k.includes("refreshToken")
  );
  console.log(
    "kinde refresh token:",
    refreshTokenKey ? window.localStorage.getItem(refreshTokenKey) : null
  );
}

const isSurveyRoute = window.location.pathname.startsWith("/survey/");

const root = ReactDOM.createRoot(document.getElementById("root")!);
root.render(
  isSurveyRoute ? (
    <ThemeProvider>
      <GlobalStyle />
      <AppRoutes />
    </ThemeProvider>
  ) : (
    <KindeRedirectProvider>
      <KindeAuthenticatedUI
        kindeClientId={KINDE_ENVIRONMENT.KINDE_CUSTOMER_APP_CLIENT_ID}
        useInsecureForRefreshToken={isDangerouslyUseLocalStorage}
      >
        <QueryClientProvider client={queryClient}>
          <ThemeProvider>
            <GlobalStyle />
            <ReloginProvider>
              <UserProvider>
                <AppRoutes />
              </UserProvider>
            </ReloginProvider>
          </ThemeProvider>
        </QueryClientProvider>
      </KindeAuthenticatedUI>
    </KindeRedirectProvider>
  )
);
